import {
  Box,
  Text,
  Textarea,
  Avatar,
  Stack,
  Flex,
  Button,
} from '@chakra-ui/react';
import { memo, useEffect, ReactElement } from 'react';
import { GetAuthData } from '../../../../app/misc/GetAuthData';
import { useDiskusiContext } from '../../controller/pengetahuan/diskusi';
import Pagination from '../Pagination';

type DiskusiProps = {
  readOnly?: boolean;
  noWrapper?: boolean;
  pengetahuanId: number;
};

const Diskusi = memo(
  ({ readOnly = false, noWrapper = false, pengetahuanId }: DiskusiProps) => {
    const { state, fetchDiskusi, handleSubmit, setDiskusiState } = useDiskusiContext();
    const { diskusiList, message, isLoading, isSubmitting, page, totalPage } = state;
    const userData = GetAuthData();

    useEffect(() => {
      if (pengetahuanId) {
        fetchDiskusi(pengetahuanId);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pengetahuanId, page]);

    const handlePageChange = (e) => {
      setDiskusiState('page', e.selected + 1);
    };

    const renderHeader = (): ReactElement | null => {
      if (noWrapper) return null;
      // @ts-ignore - Chakra UI type complexity issue
      return (
        <Box p={6} borderBottom="1px" borderColor="gray.200">
          <Text fontSize="lg" fontWeight="semibold" color="gray.800">
            Kolom Diskusi
          </Text>
        </Box>
      ) as ReactElement;
    };

    const renderInputForm = (): ReactElement | null => {
      if (readOnly) return null;
      return (
        // @ts-ignore - Chakra UI type complexity issue
        <Flex align="start" mb={4}>
          <Avatar
            size="md"
            name={userData?.username || 'User'}
            bg="blue.500"
            color="white"
            mr={3}
            fontWeight="bold"
          />
          <Box flex="1">
            <Textarea
              placeholder="Tulis sesuatu disini..."
              size="md"
              resize="vertical"
              rows={3}
              bg="gray.50"
              border="1px"
              borderColor="gray.300"
              _focus={{ borderColor: 'blue.500', bg: 'white' }}
              value={message}
              onChange={(e) => setDiskusiState('message', e.target.value)}
              isDisabled={isSubmitting}
            />
            <Flex mt={4} justify="flex-end">
              <Button
                colorScheme="green"
                size="sm"
                isDisabled={!state.message.trim()}
                onClick={() => handleSubmit(pengetahuanId)}
                isLoading={isSubmitting}
              >
                Kirim
              </Button>
            </Flex>
          </Box>
        </Flex>
      ) as ReactElement;
    };

    const renderDiskusiList = (): ReactElement => {
      if (isLoading) {
        // @ts-ignore - Chakra UI type complexity issue
        return (
          <Text color="gray.500">Memuat diskusi...</Text>
        ) as ReactElement;
      }

      if (diskusiList.length === 0) {
        // @ts-ignore - Chakra UI type complexity issue
        return (
          <Text color="gray.500">Belum ada diskusi</Text>
        ) as ReactElement;
      }

      // @ts-ignore - Chakra UI type complexity issue
      return (
        <Stack spacing={4}>
          {diskusiList.map((diskusi) => (
            <Box key={diskusi.id}>
              <Flex align="start" mb={2}>
                <Avatar
                  size="md"
                  name={diskusi.username}
                  bg="green.500"
                  color="white"
                  mr={3}
                  fontWeight="bold"
                />
                <Box flex="1">
                  <Text fontSize="sm" fontWeight="semibold" color="gray.900">
                    {diskusi.username}
                  </Text>
                  <Text fontWeight="semibold" color="gray.500">
                    {diskusi.created_at}
                  </Text>
                  <Text fontSize="sm" color="gray.700" lineHeight="1.5">
                    {diskusi.message}
                  </Text>
                </Box>
              </Flex>
            </Box>
          ))}
        </Stack>
      ) as ReactElement;
    };

    const content = (
      <>
        {renderHeader()}
        <Box p={noWrapper ? 0 : 6}>
          {renderInputForm()}
          {renderDiskusiList()}
          {totalPage > 1 && (
            <Flex justifyContent="center" mt={6}>
              <Pagination
                handleChange={handlePageChange}
                totalPage={totalPage}
                page={page}
              />
            </Flex>
          )}
        </Box>
      </>
    );

    return noWrapper ? (
      content
    ) : (
      <Box
        w="full"
        bg="white"
        rounded="lg"
        shadow="sm"
        border="1px"
        borderColor="gray.200"
        mt={3}
      >
        {content}
      </Box>
    );
  }
);

Diskusi.displayName = 'Diskusi';

export default Diskusi;
